import React, { useEffect, useMemo, useRef, useState } from "react";

import "../assets/css/HotelManagement.css";
import HeaderBlue from "../components/HeaderBlue";
import Footer from "../components/Footer";
import banner from "../assets/img/hm-banner.png"
import hospitalityContactBackground from "../assets/img/hm.png"

import Faculty from "../school-components/Faculty";
import Alumni from "../home-components/Alumni";

const highlights = [
    { value: "01", title: "Industry-Led Curriculum", text: "Learning shaped by contemporary hospitality operations and service standards." },
    { value: "02", title: "Experiential Training", text: "Practical exposure across front office, culinary, service and event operations." },
    { value: "03", title: "Global Career Pathways", text: "Preparation for careers in hotels, tourism, aviation, events and luxury services." },
    { value: "04", title: "Professional Mentoring", text: "Academic guidance, internship support and leadership-oriented development." },
];

const programs = [
    {
        name: "doctor-of-philosophy-in-hotel-management-and-catering-technology",
        title: "Doctor of Philosophy (Ph.D.) in Hotel Management and Catering Technology",
        level: "Doctorate",
        duration: "3 Years",
        eligibility: "Post Graduation",
        mode: "Semester",
        description:
            "A research-oriented doctoral programme designed to develop advanced expertise in hospitality management, service innovation, tourism, and catering technology.",
    },
    {
        name: "master-of-hotel-management-and-catering-technology",
        title: "Master of Hotel Management and Catering Technology (Proposed)",
        level: "Postgraduate",
        duration: "2 Years",
        eligibility: "Graduation or Equivalent (with respective subjects)",
        mode: "Semester",
        description:
            "An advanced hospitality programme focused on hotel operations, strategic management, guest experience, and leadership across hotels, resorts, and allied service sectors.",
    },
    {
        name: "msc-hotel-management",
        title: "M.Sc. in Hotel Management",
        level: "Postgraduate",
        duration: "2 Years",
        eligibility: "Graduation in B.Sc. Hotel Management or Hospitality Studies",
        mode: "Semester",
        description:
            "A postgraduate programme designed to build advanced expertise in hospitality operations, strategic management, guest experience, and service innovation.",
    },
    {
        name: "bachelor-of-hotel-management-and-catering-technology",
        title: "Bachelor of Hotel Management and Catering Technology (BHMCT)",
        level: "Undergraduate",
        duration: "4 Years",
        eligibility: "10+2 or equivalent (Any Stream)",
        mode: "Semester",
        description:
            "A comprehensive professional programme covering hotel operations, food production, food and beverage service, front office, housekeeping, and hospitality leadership.",
    },
    {
        name: "bsc-hotel-management",
        title: "B.Sc. in Hotel Management",
        level: "Undergraduate",
        duration: "3 Years",
        eligibility: "10+2 or Equivalent",
        mode: "Semester",
        description:
            "A career-focused degree that prepares students for supervisory and managerial roles in hospitality, travel, and tourism through practical and managerial learning.",
    },
    {
        name: "diploma-in-hotel-management-and-catering-technology",
        title: "Diploma in Hotel Management and Catering Technology (DHMCT)",
        level: "Diploma",
        duration: "3 Years",
        eligibility: "10+2 or equivalent (Any Stream)",
        mode: "Semester",
        description:
            "A practical diploma programme that develops the operational knowledge, service skills, professional values, and industry readiness required in hotel management.",
    },
    {
        name: "diploma-in-culinary-arts",
        title: "Diploma in Culinary Arts",
        level: "Diploma",
        duration: "1 Year",
        eligibility: "10+2 or equivalent (Any Stream)",
        mode: "Annual",
        description:
            "A hands-on culinary programme covering food preparation, cooking techniques, kitchen operations, nutrition, hygiene, presentation, and culinary entrepreneurship.",
    },
    {
        name: "diploma-in-travel-and-tourism",
        title: "Diploma in Travel & Tourism",
        level: "Diploma",
        duration: "1 Year",
        eligibility: "10+2 or equivalent (Any Stream)",
        mode: "Annual",
        description:
            "A focused programme providing foundational knowledge of tourism operations, travel services, destination planning, customer service, and the global tourism industry.",
    },
];

const levelOrder = ["All Programs", "Undergraduate", "Postgraduate", "Diploma", "Doctorate"];

const ArrowUpRightIcon = ({ className = "h-5 w-5" }) => (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M8 7h9v9" />
    </svg>
);

const ClockIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
        <circle cx="12" cy="12" r="8.5" />
        <path strokeLinecap="round" d="M12 7.5V12l3 2" />
    </svg>
);

const GraduationIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="m3 9 9-5 9 5-9 5-9-5Z" />
        <path strokeLinecap="round" d="M7 11.5V16c2.7 2 7.3 2 10 0v-4.5M21 9v6" />
    </svg>
);

const CalendarIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
        <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
        <path strokeLinecap="round" d="M8 3.5v4M16 3.5v4M3.5 9.5h17" />
    </svg>
);

function ProgramCard({ program, index, courseBasePath }) {
    const href = `${courseBasePath.replace(/\/$/, "")}/${program.name}`;

    return (
        <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-[#071A2F]/10 bg-white p-6 shadow-[0_20px_60px_-36px_rgba(7,26,47,0.45)] transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57]/70 hover:shadow-[0_30px_80px_-38px_rgba(7,26,47,0.6)] sm:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#071A2F] via-[#D5AD57] to-[#8B1E3F] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            <div className="mb-6 flex items-start justify-between gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#071A2F] text-sm font-semibold tracking-[0.14em] text-white shadow-lg shadow-[#071A2F]/15">
                    {String(index + 1).padStart(2, "0")}
                </div>
                <span className="rounded-full border border-[#D5AD57]/40 bg-[#F7F2E8] px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                    {program.level}
                </span>
            </div>

            <div className="flex-1">
                <h3 className="text-xl font-semibold leading-snug text-[#071A2F] transition-colors group-hover:text-[#8B1E3F] sm:text-[1.35rem]">
                    {program.title}
                </h3>
                <p className="mt-4 line-clamp-3 text-sm leading-7 text-[#132337]/70">{program.description}</p>

                <dl className="mt-6 grid gap-3 border-t border-[#071A2F]/10 pt-5">
                    <div className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 text-[#A77B2D]"><ClockIcon /></span>
                        <div><dt className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#132337]/45">Duration</dt><dd className="mt-1 font-medium text-[#132337]">{program.duration}</dd></div>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 text-[#A77B2D]"><GraduationIcon /></span>
                        <div><dt className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#132337]/45">Eligibility</dt><dd className="mt-1 font-medium leading-6 text-[#132337]">{program.eligibility}</dd></div>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 text-[#A77B2D]"><CalendarIcon /></span>
                        <div><dt className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#132337]/45">Academic Pattern</dt><dd className="mt-1 font-medium text-[#132337]">{program.mode}</dd></div>
                    </div>
                </dl>
            </div>

            <a href={href} aria-label={`View details for ${program.title}`} className="mt-7 inline-flex items-center justify-between rounded-2xl bg-[#071A2F] px-5 py-3.5 text-sm font-semibold text-white transition duration-300 hover:bg-[#8B1E3F] focus:outline-none focus:ring-2 focus:ring-[#D5AD57] focus:ring-offset-2">
                <span>View Programme</span>
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
        </article>
    );
}

const learningMethods = [
    {
        number: "01",
        title: "Industry-Oriented Learning",
        text: "Curriculum aligned with contemporary practices in software engineering, cloud computing, cybersecurity, analytics and digital transformation.",
        outcome: "Industry relevance",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <rect x="3.5" y="7" width="17" height="12.5" rx="2" />
                <path d="M8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7M3.5 12h17M10 12v2h4v-2" />
            </svg>
        ),
    },
    {
        number: "02",
        title: "Project-Based Education",
        text: "Students apply classroom knowledge through software products, data solutions, applications and interdisciplinary technology projects.",
        outcome: "Applied capability",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <path d="M4 5.5h16v13H4zM8 9h8M8 12h5M8 15h7" />
            </svg>
        ),
    },
    {
        number: "03",
        title: "AI & Emerging Technologies",
        text: "Learning integrates artificial intelligence, machine learning, data science, IoT, automation and next-generation digital systems.",
        outcome: "Future readiness",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <rect x="6" y="6" width="12" height="12" rx="3" />
                <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4M9.5 10.5h5v3h-5z" />
            </svg>
        ),
    },
    {
        number: "04",
        title: "Research & Innovation",
        text: "Students are encouraged to investigate real-world challenges, experiment with ideas and develop evidence-led technological solutions.",
        outcome: "Innovation mindset",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <path d="M9 3h6M10 3v5l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3M7.5 15h9" />
            </svg>
        ),
    },
    {
        number: "05",
        title: "Hands-on Laboratories",
        text: "Technology laboratories provide practical exposure to programming, networking, cloud platforms, databases, security and intelligent systems.",
        outcome: "Technical confidence",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <rect x="3.5" y="4" width="17" height="12" rx="2" />
                <path d="M8 20h8M12 16v4M7 8h3M7 11h5" />
            </svg>
        ),
    },
    {
        number: "06",
        title: "Internships & Collaboration",
        text: "Internships, expert sessions and industry collaboration connect academic learning with professional practices and workplace expectations.",
        outcome: "Career exposure",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                className="h-6 w-6"
            >
                <circle cx="8" cy="8" r="3" />
                <circle cx="17" cy="9" r="2.5" />
                <path d="M3.5 20a4.5 4.5 0 0 1 9 0M13.5 20a3.5 3.5 0 0 1 7 0" />
            </svg>
        ),
    },
];

const schoolOutcomes = [
    {
        number: "01",
        value: "Career",
        label: "High Employability",
        text: "Technical capability, communication, problem-solving and professional readiness for technology-driven roles.",
    },
    {
        number: "02",
        value: "Certified",
        label: "Industry Certifications",
        text: "Opportunities to strengthen professional profiles through relevant technology tools, platforms and certification pathways.",
    },
    {
        number: "03",
        value: "Venture",
        label: "Startup & Entrepreneurship",
        text: "Innovation, design thinking and business guidance help students convert technology ideas into viable solutions.",
    },
    {
        number: "04",
        value: "Global",
        label: "Career Opportunities",
        text: "Preparation for opportunities across software, analytics, cloud, cybersecurity, AI and digital transformation domains.",
    },
    {
        number: "05",
        value: "Research",
        label: "Publications & Projects",
        text: "Students develop structured inquiry, technical writing and project presentation capabilities through research-led learning.",
    },
    {
        number: "06",
        value: "Advance",
        label: "Higher Studies & Innovation",
        text: "Strong academic foundations support postgraduate education, research pathways and advanced specialisation.",
    },
];

const hospitalityPlacementStats = [
    {
        value: "45",
        suffix: " LPA",
        label: "Highest placement opportunity",
    },
    {
        value: "250",
        suffix: "+",
        label: "Recruitment partners",
    },
    {
        value: "95",
        suffix: "%",
        label: "Placement support participation",
    },
    {
        value: "20",
        suffix: "+",
        label: "Hospitality career sectors",
    },
];

const hospitalityCareerPath = [
    {
        number: "01",
        title: "Career Discovery",
        text: "Career counselling helps students identify suitable pathways across hotels, culinary operations, tourism, aviation and service management.",
    },
    {
        number: "02",
        title: "Professional Preparation",
        text: "Communication labs, grooming, resume development and interview practice strengthen professional confidence.",
    },
    {
        number: "03",
        title: "Industry Exposure",
        text: "Hotel visits, guest lectures, live projects and internships connect academic learning with workplace expectations.",
    },
    {
        number: "04",
        title: "Recruitment Readiness",
        text: "Mock interviews, group discussions and role-specific guidance prepare students for hospitality recruitment processes.",
    },
    {
        number: "05",
        title: "Career Launch",
        text: "Placement coordination connects eligible students with hotels, resorts, restaurants, travel companies and service organisations.",
    },
];

const hospitalityRecruiterTypes = [
    ["HT", "Luxury Hotels"],
    ["RS", "International Resorts"],
    ["FB", "Food & Beverage Brands"],
    ["AV", "Airlines & Aviation"],
    ["CR", "Cruise Hospitality"],
    ["TR", "Travel & Tourism"],
    ["EV", "Events & Banquets"],
    ["CX", "Customer Experience"],
];

const hospitalityAdmissionSteps = [
    {
        number: "01",
        title: "Application Submission",
        text: "Complete the online application with accurate personal, contact and academic details.",
    },
    {
        number: "02",
        title: "Document Verification",
        text: "Submit academic records, identity proof, photographs and applicable supporting documents.",
    },
    {
        number: "03",
        title: "Admission Counselling",
        text: "Discuss programme options, career pathways, eligibility and the learning experience with the admissions team.",
    },
    {
        number: "04",
        title: "Admission Confirmation",
        text: "Eligible applicants receive admission confirmation and enrolment instructions.",
    },
    {
        number: "05",
        title: "Fee Payment",
        text: "Complete the prescribed payment process within the communicated admission timeline.",
    },
    {
        number: "06",
        title: "Orientation",
        text: "Begin your university journey through academic orientation and an introduction to hospitality learning.",
    },
];

const hospitalityEligibility = [
    {
        level: "Undergraduate",
        programmes: "BHMCT and B.Sc. Hotel Management",
        qualification: "10+2 or equivalent",
        guidance:
            "Applicants should have completed the qualifying examination from a recognised board under applicable University norms.",
    },
    {
        level: "Postgraduate",
        programmes: "MHMCT and M.Sc. Hotel Management",
        qualification: "Relevant bachelor’s degree",
        guidance:
            "Applicants should hold an eligible undergraduate qualification and satisfy current programme requirements.",
    },
    {
        level: "Diploma",
        programmes: "Hotel Management, Culinary Arts and Tourism",
        qualification: "As prescribed for the selected programme",
        guidance:
            "Programme-specific entry requirements should be confirmed with the admissions team before applying.",
    },
    {
        level: "Doctoral",
        programmes: "Ph.D. in Hotel Management",
        qualification: "Relevant postgraduate degree",
        guidance:
            "Admission remains subject to research eligibility, entrance, interview and applicable regulatory requirements.",
    },
];

const hospitalityFaqs = [
    {
        category: "Eligibility",
        question: "What is the eligibility for hotel management programmes?",
        answer:
            "Eligibility depends on the selected programme. Undergraduate applicants generally require 10+2 or an equivalent qualification, while postgraduate and doctoral programmes require an eligible degree in a relevant area. Final admission is subject to current University norms.",
    },
    {
        category: "Duration",
        question: "What is the duration of the hotel management courses?",
        answer:
            "Programme duration varies by academic level. Diploma programmes may range from one to two years, undergraduate programmes generally extend across three or four years, and postgraduate or doctoral programmes follow their respective academic structures.",
    },
    {
        category: "Placements",
        question: "Does the School provide placement assistance?",
        answer:
            "Career support may include professional grooming, communication training, resume development, aptitude preparation, mock interviews, internship guidance and placement coordination. Participation remains subject to applicable placement policies.",
    },
    {
        category: "Internships",
        question: "Are internships included in the learning experience?",
        answer:
            "Internships and practical industry exposure form an important part of hospitality education. Their duration, assessment structure and organisation depend on the selected programme and current academic regulations.",
    },
    {
        category: "Facilities",
        question: "Are hostel facilities available for students?",
        answer:
            "Students may enquire about current hostel availability, accommodation guidelines, fees and applicable facilities through the University admissions or student support team.",
    },
    {
        category: "Scholarships",
        question: "Are scholarships available?",
        answer:
            "Scholarship opportunities may be available according to current University policies, academic merit, eligibility criteria and the applicable admission session.",
    },
    {
        category: "Global Careers",
        question: "Can graduates explore international hospitality careers?",
        answer:
            "Hospitality graduates may explore careers across hotels, resorts, airlines, cruise services, tourism, food service and customer-experience organisations. Opportunities depend on individual skills, experience, language ability and employer requirements.",
    },
];

function HotelManagement() {

    const courseBasePath = "/programmes";
    const heading = "Academic Programs";
    const eyebrow = "Study pathways";
    const [activeLevel, setActiveLevel] = useState("All Programs");

    const counts = useMemo(() => programs.reduce((acc, program) => {
        acc[program.level] = (acc[program.level] || 0) + 1;
        return acc;
    }, { "All Programs": programs.length }), []);

    const filteredPrograms = useMemo(() => activeLevel === "All Programs" ? programs : programs.filter((program) => program.level === activeLevel), [activeLevel]);

    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [openHospitalityFaq, setOpenHospitalityFaq] = useState(0);
    const [hospitalityFaqSearch, setHospitalityFaqSearch] = useState("");

    const visibleHospitalityFaqs = hospitalityFaqs.filter((item) => {
        const term = hospitalityFaqSearch.trim().toLowerCase();

        return (
            !term ||
            item.question.toLowerCase().includes(term) ||
            item.answer.toLowerCase().includes(term) ||
            item.category.toLowerCase().includes(term)
        );
    });

    return (
        <>
            <HeaderBlue />
            <main>
                <section className="hm-hero" aria-labelledby="hm-hero-title">
                    <div className="hm-grid-pattern" aria-hidden="true" />
                    <div className="container-fluid hm-container">
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-1/2 xl:w-5/12">
                                <div className="hm-content">
                                    <div className="hm-kicker">
                                        <span className="hm-kicker-line" aria-hidden="true" />
                                        School of Hotel Management
                                    </div>

                                    <h1 id="hm-hero-title" className="hm-title">
                                        Shape the Future of <em>Global Hospitality</em>
                                    </h1>

                                    <p className="hm-description">
                                        Develop the operational expertise, service intelligence and leadership perspective
                                        required for luxury hospitality, culinary arts, tourism, event management and
                                        exceptional guest experience.
                                    </p>

                                    <div className="hm-actions" aria-label="School actions">
                                        <a href="#programs" className="hm-btn hm-btn-primary">
                                            Explore Programs
                                            <i className="bi bi-arrow-up-right" aria-hidden="true" />
                                        </a>
                                        <a href="#apply" className="hm-btn hm-btn-secondary">
                                            Apply Now
                                            <i className="bi bi-chevron-right" aria-hidden="true" />
                                        </a>
                                    </div>

                                    <div className="hm-proof" aria-label="Academic approach">
                                        <div className="hm-proof-mark">
                                            <i className="bi bi-mortarboard" aria-hidden="true" />
                                        </div>
                                        <div>
                                            <span>University-led hospitality education</span>
                                            <strong>Academic excellence with industry immersion</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full lg:w-1/2 xl:w-7/12">
                                <div className="hm-visual">
                                    <div className="hm-image-frame">
                                        <img
                                            src={banner}
                                            alt="Premium contemporary hotel interior representing professional hospitality education"
                                            className="hm-image"
                                        />
                                        <div className="hm-image-overlay" aria-hidden="true" />
                                        <div className="hm-vertical-caption" aria-hidden="true">
                                            Hospitality · Leadership · Excellence
                                        </div>

                                        <article className="hm-feature-card hm-feature-card-main">
                                            <span className="hm-feature-label">Academic Experience</span>
                                            <h2>Learn through real-world hospitality environments</h2>
                                            <div className="hm-feature-meta">
                                                <span><i className="bi bi-building" /> Hotel Operations</span>
                                                <span><i className="bi bi-cup-hot" /> Culinary &amp; Service</span>
                                                <span><i className="bi bi-globe2" /> Tourism &amp; Events</span>
                                            </div>
                                        </article>

                                        <article className="hm-feature-card hm-feature-card-small">
                                            <div className="hm-small-icon"><i className="bi bi-stars" /></div>
                                            <div>
                                                <span>Professional Standard</span>
                                                <strong>Guest-first leadership</strong>
                                            </div>
                                        </article>
                                    </div>

                                    <div className="hm-gold-panel" aria-hidden="true" />
                                    <div className="hm-outline-panel" aria-hidden="true" />
                                </div>
                            </div>
                        </div>

                        <div className="hm-highlights" aria-label="School highlights">
                            {highlights.map((item) => (
                                <article className="hm-highlight" key={item.value}>
                                    <span className="hm-highlight-number">{item.value}</span>
                                    <div>
                                        <h3>{item.title}</h3>
                                        <p>{item.text}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Academic Programs */}
                <section
                    id="academic-programs"
                    aria-labelledby="academic-programs-heading"
                    className="relative isolate overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-[110px]"
                >
                    {/* Background treatment */}
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_12%,rgba(139,30,63,0.32),transparent_28%)]" />

                        <div className="absolute -left-32 top-1/3 h-80 w-80 rounded-full bg-[#D5AD57]/[0.07] blur-[90px]" />

                        <div className="absolute -right-28 bottom-0 h-96 w-96 rounded-full bg-[#8B1E3F]/15 blur-[100px]" />

                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57]/45 to-transparent" />

                        <div className="absolute -bottom-16 right-0 select-none font-sans text-[10rem] font-black leading-none tracking-[-0.08em] text-white/[0.018] sm:text-[15rem] lg:text-[22rem]">
                            STUDY
                        </div>

                        <svg
                            aria-hidden="true"
                            viewBox="0 0 500 500"
                            fill="none"
                            className="absolute -right-24 top-12 hidden h-[35rem] w-[35rem] text-white/[0.035] lg:block"
                        >
                            <circle cx="250" cy="250" r="180" stroke="currentColor" />
                            <circle cx="250" cy="250" r="125" stroke="currentColor" />
                            <path d="M70 250h360M250 70v360" stroke="currentColor" />
                        </svg>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        {/* Header */}
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>{eyebrow}</span>
                                </div>

                                <h2
                                    id="academic-programs-heading"
                                    className="m-0 max-w-[850px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-white"
                                >
                                    Explore programmes designed for
                                    <em className="block font-normal text-[#D5AD57] sm:inline">
                                        {" "}
                                        hospitality leadership.
                                    </em>
                                </h2>
                            </div>

                            <div className="border-l border-white/15 pl-7 max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                <p className="m-0 text-[15px] leading-[1.85] text-[#B5C0CC]">
                                    Build knowledge and practical competence through programmes covering
                                    hotel operations, culinary arts, catering technology, tourism,
                                    service management and hospitality research.
                                </p>
                            </div>
                        </header>

                        {/* Program summary */}
                        <div className="mt-14 grid overflow-hidden rounded-[7px_34px_7px_7px] border border-white/15 bg-white/[0.055] backdrop-blur-xl sm:grid-cols-[220px_1fr]">
                            <div className="flex min-h-[145px] flex-col justify-center border-b border-white/15 px-7 py-7 sm:border-b-0 sm:border-r sm:px-8">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    Programmes offered
                                </span>

                                <strong className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-6xl font-normal leading-none tracking-[-0.05em] text-white">
                                    {programs.length}
                                </strong>
                            </div>

                            <div className="grid gap-6 px-7 py-7 sm:grid-cols-2 sm:px-9">
                                <div>
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-white/45">
                                        Academic levels
                                    </span>

                                    <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-white">
                                        Multiple entry pathways
                                    </p>
                                </div>

                                <p className="m-0 self-end text-sm leading-7 text-[#9EABB8]">
                                    Undergraduate, postgraduate, diploma and doctoral opportunities
                                    designed for different stages of academic and professional growth.
                                </p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="mt-12 border-y border-white/15 py-5">
                            <div
                                role="tablist"
                                aria-label="Filter academic programmes by level"
                                className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            >
                                {levelOrder.map((level) => {
                                    const isActive = activeLevel === level;
                                    const count = counts[level] || 0;

                                    return (
                                        <button
                                            key={level}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveLevel(level)}
                                            className={[
                                                "inline-flex min-h-[48px] shrink-0 items-center gap-3 rounded-full border px-5 text-[12px] font-extrabold tracking-[0.02em] transition duration-300",
                                                "focus:outline-none focus:ring-2 focus:ring-[#D5AD57] focus:ring-offset-2 focus:ring-offset-[#071A2F]",
                                                isActive
                                                    ? "border-[#8B1E3F] bg-[#8B1E3F] text-white shadow-[0_14px_35px_rgba(139,30,63,0.3)]"
                                                    : "border-white/15 bg-white/[0.035] text-white/65 hover:border-[#D5AD57]/60 hover:bg-white/[0.07] hover:text-white",
                                            ].join(" ")}
                                        >
                                            <span>{level}</span>

                                            <span
                                                className={[
                                                    "grid min-w-6 place-items-center rounded-full px-1.5 py-0.5 text-[10px]",
                                                    isActive
                                                        ? "bg-white/15 text-white"
                                                        : "bg-white/[0.08] text-[#D5AD57]",
                                                ].join(" ")}
                                            >
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Program cards */}
                        <div
                            role="tabpanel"
                            className="mt-10 overflow-hidden rounded-[8px_38px_8px_8px] border border-white/15 bg-white/[0.035]"
                        >
                            {filteredPrograms.map((program, index) => (
                                <article
                                    key={program.name}
                                    className="group relative grid gap-6 border-b border-white/15 px-6 py-7 transition duration-300 last:border-b-0 hover:bg-white/[0.055] sm:px-8 sm:py-8 lg:grid-cols-[90px_minmax(170px,0.32fr)_minmax(0,1fr)_58px] lg:items-center lg:gap-8"
                                >
                                    {/* Programme number */}
                                    <div className="flex items-center justify-between lg:block">
                                        <span className="font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal tracking-[-0.04em] text-[#D5AD57] lg:text-5xl">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>

                                        <span className="rounded-full border border-white/15 px-3 py-1 text-[9px] font-extrabold uppercase tracking-[0.12em] text-white/55 lg:hidden">
                                            {program.mode}
                                        </span>
                                    </div>

                                    {/* Programme level */}
                                    <div className="lg:border-l lg:border-white/15 lg:pl-8">
                                        <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                            {program.level}
                                        </span>

                                        <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-white">
                                            {program.duration}
                                        </p>

                                        <span className="mt-2 hidden w-fit rounded-full border border-white/15 px-3 py-1 text-[9px] font-extrabold uppercase tracking-[0.12em] text-white/45 lg:inline-flex">
                                            {program.mode}
                                        </span>
                                    </div>

                                    {/* Programme content */}
                                    <div className="lg:border-l lg:border-white/15 lg:pl-8">
                                        <h3 className="max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-[26px] font-normal leading-[1.2] text-white transition-colors duration-300 group-hover:text-[#D5AD57] sm:text-[29px]">
                                            {program.title}
                                        </h3>

                                        <p className="mt-4 max-w-3xl text-[13px] leading-[1.8] text-[#AAB6C2]">
                                            {program.description}
                                        </p>

                                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                            <div className="border-l-2 border-[#D5AD57] pl-4">
                                                <span className="text-[9px] font-extrabold uppercase tracking-[0.13em] text-white/40">
                                                    Duration
                                                </span>

                                                <p className="mt-1 text-sm font-semibold text-white">
                                                    {program.duration}
                                                </p>
                                            </div>

                                            <div className="border-l-2 border-[#8B1E3F] pl-4">
                                                <span className="text-[9px] font-extrabold uppercase tracking-[0.13em] text-white/40">
                                                    Eligibility
                                                </span>

                                                <p className="mt-1 text-sm leading-6 text-white/70">
                                                    {program.eligibility}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Link */}
                                    <div className="flex justify-end lg:block">
                                        <a
                                            href={`${courseBasePath.replace(/\/$/, "")}/${program.name}`}
                                            aria-label={`View ${program.title}`}
                                            className="grid h-12 w-12 place-items-center rounded-full border border-white/20 text-lg text-[#D5AD57] transition duration-300 hover:border-[#D5AD57] hover:bg-[#D5AD57] hover:text-[#071A2F] group-hover:translate-x-1 group-hover:-translate-y-1"
                                        >
                                            ↗
                                        </a>
                                    </div>

                                    {/* Hover accent */}
                                    <div className="absolute inset-y-0 left-0 w-1 origin-bottom scale-y-0 bg-[#8B1E3F] transition-transform duration-300 group-hover:scale-y-100" />
                                </article>
                            ))}
                        </div>

                        {/* Guidance panel */}
                        <div className="mt-12 grid overflow-hidden rounded-[7px_30px_7px_7px] border border-white/15 bg-[#0C2744] sm:grid-cols-[1fr_auto] sm:items-center">
                            <div className="px-6 py-7 sm:px-8 lg:px-10">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#D5AD57]">
                                    Need programme guidance?
                                </span>

                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-snug text-white sm:text-3xl">
                                    Find the right hospitality pathway for your career.
                                </h3>
                            </div>

                            <div className="border-t border-white/15 px-6 py-6 sm:border-l sm:border-t-0 sm:px-8">
                                <a
                                    href="/contact-us"
                                    className="inline-flex min-h-[52px] items-center justify-center gap-5 rounded-full bg-[#D5AD57] px-6 text-[12px] font-extrabold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0C2744]"
                                >
                                    Speak to Admissions
                                    <span aria-hidden="true">↗</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Choose ISBM Hotel Management School */}
                <section
                    id="why-choose-hotel-management"
                    aria-labelledby="why-choose-heading"
                    className="relative isolate overflow-hidden bg-[#F4E8EC] py-20 text-[#132337] sm:py-24 lg:py-[110px]"
                >
                    {/* Background treatment */}
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_14%,rgba(139,30,63,0.12),transparent_28%)]" />

                        <div className="absolute -left-28 bottom-0 h-80 w-80 rounded-full bg-[#D5AD57]/10 blur-[90px]" />

                        <div className="absolute -right-24 top-16 h-72 w-72 rounded-full bg-[#8B1E3F]/10 blur-[90px]" />

                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B1E3F]/30 to-transparent" />

                        <div className="absolute -bottom-12 right-0 select-none font-sans text-[9rem] font-black leading-none tracking-[-0.08em] text-[#071A2F]/[0.025] sm:text-[14rem] lg:text-[19rem]">
                            WHY
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        {/* Header */}
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>The ISBM advantage</span>
                                </div>

                                <h2
                                    id="why-choose-heading"
                                    className="m-0 max-w-[880px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-[#071A2F]"
                                >
                                    Why choose ISBM
                                    <em className="font-normal text-[#8B1E3F]">
                                        {" "}
                                        Hotel Management School?
                                    </em>
                                </h2>
                            </div>

                            <p className="m-0 border-l border-[#071A2F]/15 pl-7 text-[15px] leading-[1.85] text-[#586575] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                Build professional expertise through academic learning, practical
                                hospitality training, industry exposure and leadership-focused
                                education designed for contemporary hospitality careers.
                            </p>
                        </header>

                        {/* Main feature */}
                        <div className="mt-16 grid overflow-hidden rounded-[8px_42px_8px_8px] border border-[#071A2F]/12 bg-white/70 shadow-[0_30px_90px_rgba(7,26,47,0.1)] backdrop-blur-xl lg:grid-cols-[0.82fr_1.18fr]">
                            {/* Dark editorial feature */}
                            <article className="relative overflow-hidden bg-[#071A2F] px-7 py-10 text-white sm:px-10 sm:py-12 lg:px-12 lg:py-14">
                                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border border-white/10" />

                                <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full border border-[#D5AD57]/20" />

                                <div className="absolute bottom-0 left-0 h-44 w-44 rounded-tr-full bg-[#8B1E3F]/20" />

                                <div className="relative flex h-full flex-col">
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#D5AD57]">
                                        Learning beyond the classroom
                                    </span>

                                    <h3 className="mt-6 max-w-md font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(32px,3.2vw,46px)] font-normal leading-[1.15]">
                                        Professional hospitality education shaped by practice.
                                    </h3>

                                    <p className="mt-6 max-w-lg text-[14px] leading-[1.85] text-[#AAB6C2]">
                                        Students gain exposure to hotel operations, food production,
                                        restaurant service, guest relations, front office management,
                                        tourism and service leadership within a professionally structured
                                        academic environment.
                                    </p>

                                    <div className="mt-10 grid grid-cols-2 border-l border-t border-white/15">
                                        {[
                                            ["Practical", "Training"],
                                            ["Industry", "Orientation"],
                                            ["Career", "Readiness"],
                                            ["Service", "Leadership"],
                                        ].map(([title, subtitle]) => (
                                            <div
                                                key={`${title}-${subtitle}`}
                                                className="min-h-[105px] border-b border-r border-white/15 p-5 transition duration-300 hover:bg-white/[0.045]"
                                            >
                                                <strong className="block font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-white">
                                                    {title}
                                                </strong>

                                                <span className="mt-2 block text-[10px] font-bold uppercase tracking-[0.1em] text-[#D5AD57]">
                                                    {subtitle}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <a
                                        href="#academic-programs"
                                        className="mt-10 inline-flex w-fit items-center gap-6 border-b border-[#D5AD57] pb-2 text-[11px] font-extrabold uppercase tracking-[0.09em] text-[#D5AD57] transition duration-300 hover:gap-8 hover:text-white"
                                    >
                                        Explore academic programmes
                                        <span aria-hidden="true">↗</span>
                                    </a>
                                </div>
                            </article>

                            {/* Benefit list */}
                            <div className="grid sm:grid-cols-2">
                                {[
                                    {
                                        number: "01",
                                        title: "Industry-Focused Curriculum",
                                        description:
                                            "Programmes reflect contemporary hotel operations, hospitality management, culinary practices, tourism and guest-service standards.",
                                    },
                                    {
                                        number: "02",
                                        title: "Hands-On Skill Development",
                                        description:
                                            "Practical learning builds competence in food production, restaurant service, front office operations and professional guest interaction.",
                                    },
                                    {
                                        number: "03",
                                        title: "Professional Learning Environment",
                                        description:
                                            "Students develop discipline, service excellence, workplace conduct and professional presentation within a mature academic setting.",
                                    },
                                    {
                                        number: "04",
                                        title: "Internship and Career Support",
                                        description:
                                            "Industry exposure and career guidance help learners understand professional expectations and prepare for diverse hospitality roles.",
                                    },
                                    {
                                        number: "05",
                                        title: "Leadership and Service Excellence",
                                        description:
                                            "The learning approach develops communication, teamwork, problem-solving, responsibility and a guest-first professional mindset.",
                                    },
                                    {
                                        number: "06",
                                        title: "Global Hospitality Career Pathways",
                                        description:
                                            "Graduates may explore opportunities across hotels, resorts, restaurants, tourism, events, airlines and customer-experience functions.",
                                    },
                                ].map((item) => (
                                    <article
                                        key={item.number}
                                        className="group relative min-h-[245px] border-b border-[#071A2F]/10 p-7 transition duration-300 hover:bg-[#F7F2E8] sm:border-r sm:p-8 sm:even:border-r-0"
                                    >
                                        <span className="text-[10px] font-extrabold text-[#A77B2D]">
                                            {item.number}
                                        </span>

                                        <h3 className="mt-7 max-w-[280px] font-['Libre_Caslon_Display',Georgia,serif] text-[24px] font-normal leading-[1.25] text-[#071A2F]">
                                            {item.title}
                                        </h3>

                                        <p className="mt-4 text-[12px] leading-[1.75] text-[#65717D]">
                                            {item.description}
                                        </p>

                                        <div className="mt-7 h-px w-10 bg-[#8B1E3F] transition-all duration-300 group-hover:w-20" />

                                        <span className="absolute right-6 top-5 font-['Libre_Caslon_Display',Georgia,serif] text-6xl font-normal text-[#071A2F]/[0.035]">
                                            {item.number}
                                        </span>
                                    </article>
                                ))}
                            </div>
                        </div>

                        {/* Bottom statement */}
                        <div className="mt-10 grid overflow-hidden rounded-[7px_28px_7px_7px] border border-[#071A2F]/12 bg-[#EEE5D3] sm:grid-cols-[1fr_auto] sm:items-center">
                            <div className="flex items-start gap-4 px-6 py-6 sm:px-8">
                                <div className="mt-1 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#8B1E3F] text-sm text-white">
                                    ✓
                                </div>

                                <div>
                                    <p className="m-0 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-[#071A2F]">
                                        A career-focused academic experience
                                    </p>

                                    <p className="mt-2 text-[12px] leading-6 text-[#65717D]">
                                        Combining academic knowledge, practical competence, professional
                                        values and service-oriented leadership.
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-[#071A2F]/10 px-6 py-5 sm:border-l sm:border-t-0 sm:px-8">
                                <a
                                    href="/apply-now"
                                    className="inline-flex min-h-[50px] items-center justify-center gap-5 rounded-full bg-[#071A2F] px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition duration-300 hover:-translate-y-1 hover:bg-[#8B1E3F] focus:outline-none focus:ring-2 focus:ring-[#D5AD57] focus:ring-offset-2 focus:ring-offset-[#EEE5D3]"
                                >
                                    Apply now
                                    <span aria-hidden="true">↗</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* From the Dean's Desk */}
                <section
                    id="deans-desk"
                    aria-labelledby="deans-desk-heading"
                    className="relative isolate overflow-hidden bg-[#EEE5D3] py-20 text-[#132337] sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_12%,rgba(139,30,63,0.1),transparent_28%)]" />

                        <div className="absolute -left-28 bottom-0 h-80 w-80 rounded-full bg-[#D5AD57]/15 blur-[90px]" />

                        <div className="absolute -right-20 top-10 h-72 w-72 rounded-full bg-[#071A2F]/[0.06] blur-[90px]" />

                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B1E3F]/30 to-transparent" />

                        <svg
                            viewBox="0 0 600 600"
                            fill="none"
                            aria-hidden="true"
                            className="absolute -right-24 top-4 hidden h-[38rem] w-[38rem] text-[#071A2F]/[0.035] lg:block"
                        >
                            <circle cx="300" cy="300" r="220" stroke="currentColor" />
                            <circle cx="300" cy="300" r="165" stroke="currentColor" />
                            <path d="M80 300h440M300 80v440" stroke="currentColor" />
                            <path d="m145 145 310 310M455 145 145 455" stroke="currentColor" />
                        </svg>

                        <div className="absolute -bottom-16 right-0 select-none font-sans text-[9rem] font-black leading-none tracking-[-0.08em] text-[#071A2F]/[0.025] sm:text-[14rem] lg:text-[20rem]">
                            DEAN
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1280px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <div className="overflow-hidden rounded-[8px_42px_8px_8px] border border-[#071A2F]/12 bg-white/75 shadow-[0_30px_90px_rgba(7,26,47,0.1)] backdrop-blur-xl">
                            <header className="grid gap-8 border-b border-[#071A2F]/10 px-6 py-9 sm:px-10 sm:py-11 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:px-16 lg:py-14">
                                <div>
                                    <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                        <span className="h-0.5 w-[38px] bg-current" />
                                        <span>From the Dean&apos;s desk</span>
                                    </div>

                                    <h2
                                        id="deans-desk-heading"
                                        className="m-0 max-w-[850px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,5vw,67px)] font-normal leading-[1.06] tracking-[-0.04em] text-[#071A2F]"
                                    >
                                        Building minds that can shape
                                        <em className="font-normal text-[#8B1E3F]">
                                            {" "}
                                            the digital future.
                                        </em>
                                    </h2>
                                </div>

                                <div className="min-w-[180px] border-l border-[#071A2F]/15 pl-6 max-lg:border-l-0 max-lg:border-t max-lg:pt-5">
                                    <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#7A8390]">
                                        School of
                                    </span>

                                    <strong className="mt-2 block font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-[#071A2F]">
                                        Information Technology
                                    </strong>
                                </div>
                            </header>

                            <div className="grid lg:grid-cols-[minmax(0,1.4fr)_360px]">
                                <article className="relative px-6 py-12 sm:px-10 sm:py-14 lg:px-16 lg:py-16">
                                    <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute right-8 top-4 font-['Libre_Caslon_Display',Georgia,serif] text-[10rem] leading-none text-[#8B1E3F]/[0.08] sm:right-14 sm:text-[13rem]"
                                    >
                                        “
                                    </span>

                                    <p className="relative z-10 max-w-[820px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(27px,3vw,43px)] font-normal leading-[1.3] text-[#071A2F]">
                                        Technology education must prepare students not merely to use
                                        digital systems, but to question, design and improve them.
                                    </p>

                                    <div className="mt-9 max-w-[850px] border-l-2 border-[#D5AD57] pl-6 sm:pl-7">
                                        <p className="m-0 text-[14px] leading-[1.85] text-[#586575]">
                                            At the School of Information Technology, we create an academic
                                            environment where computing principles meet experimentation,
                                            research and real-world application. Students learn to approach
                                            complex challenges with analytical thinking, technical
                                            discipline and professional responsibility.
                                        </p>

                                        <p className="mt-5 text-[14px] leading-[1.85] text-[#586575]">
                                            Our purpose is to nurture adaptable professionals who can work
                                            across artificial intelligence, data science, software
                                            engineering, cybersecurity, cloud systems and emerging
                                            technologies while contributing responsibly to society and
                                            industry.
                                        </p>
                                    </div>

                                    <footer className="mt-10 flex items-center gap-4 border-t border-[#071A2F]/10 pt-7">
                                        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#071A2F] font-['Libre_Caslon_Display',Georgia,serif] text-sm text-[#D5AD57]">
                                            IT
                                        </div>

                                        <div>
                                            <strong className="block text-sm font-bold text-[#071A2F]">
                                                Dean, School of Information Technology
                                            </strong>

                                            <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.11em] text-[#8B1E3F]">
                                                ISBM University
                                            </span>
                                        </div>
                                    </footer>
                                </article>

                                <aside className="relative overflow-hidden bg-[#071A2F] px-7 py-10 text-white sm:px-9 lg:px-10 lg:py-14">
                                    <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border border-white/10" />
                                    <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full border border-[#D5AD57]/20" />

                                    <div className="relative">
                                        <div className="flex justify-between gap-5 border-b border-white/15 pb-5">
                                            <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#D5AD57]">
                                                Academic direction
                                            </span>

                                            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">
                                                Three priorities
                                            </span>
                                        </div>

                                        {[
                                            [
                                                "01",
                                                "Technology with purpose",
                                                "Applying digital knowledge to meaningful institutional, social and industry challenges.",
                                            ],
                                            [
                                                "02",
                                                "Research-led learning",
                                                "Building curiosity, experimentation and evidence-based technological thinking.",
                                            ],
                                            [
                                                "03",
                                                "Responsible innovation",
                                                "Developing ethical professionals prepared for the impact of emerging technologies.",
                                            ],
                                        ].map(([number, title, text]) => (
                                            <div
                                                key={number}
                                                className="grid grid-cols-[28px_1fr] gap-5 border-b border-white/15 py-7"
                                            >
                                                <span className="pt-1 text-[10px] font-extrabold text-[#D5AD57]">
                                                    {number}
                                                </span>

                                                <div>
                                                    <h3 className="m-0 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-white">
                                                        {title}
                                                    </h3>

                                                    <p className="mt-2 text-[12px] leading-[1.7] text-[#AAB6C2]">
                                                        {text}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}

                                        <blockquote className="mb-0 mt-8 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal italic leading-[1.5] text-[#D5AD57]">
                                            “Think deeply. Build responsibly. Innovate with purpose.”
                                        </blockquote>
                                    </div>
                                </aside>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Learning Methodology */}
                <section
                    id="learning-methodology"
                    aria-labelledby="learning-methodology-heading"
                    className="relative isolate overflow-hidden bg-white py-20 text-[#132337] sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_92%_10%,rgba(213,173,87,0.12),transparent_27%)]" />

                        <div className="absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-[#8B1E3F]/[0.06] blur-[90px]" />

                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57]/50 to-transparent" />

                        <div className="absolute -bottom-12 right-0 select-none font-sans text-[8rem] font-black leading-none tracking-[-0.08em] text-[#071A2F]/[0.022] sm:text-[13rem] lg:text-[18rem]">
                            LEARN
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>Learning methodology</span>
                                </div>

                                <h2
                                    id="learning-methodology-heading"
                                    className="m-0 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-[#071A2F]"
                                >
                                    From foundational knowledge to
                                    <em className="font-normal text-[#8B1E3F]">
                                        {" "}
                                        intelligent application.
                                    </em>
                                </h2>
                            </div>

                            <p className="m-0 border-l border-[#071A2F]/15 pl-7 text-[15px] leading-[1.85] text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                Our teaching approach connects theory, laboratories, projects, research
                                and industry exposure to develop technically capable and adaptable
                                technology professionals.
                            </p>
                        </header>

                        <div className="relative mt-16">
                            <div
                                aria-hidden="true"
                                className="absolute left-[8.333%] right-[8.333%] top-[39px] hidden h-px bg-[#CFC7B8] xl:block"
                            />

                            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                                {learningMethods.map((method, index) => (
                                    <article
                                        key={method.number}
                                        className="group relative overflow-hidden rounded-[7px_30px_7px_7px] border border-[#071A2F]/10 bg-[#F7F2E8] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57] hover:bg-white hover:shadow-[0_28px_80px_-42px_rgba(7,26,47,0.55)] sm:p-7"
                                    >
                                        <div className="flex items-center justify-between gap-5">
                                            <div className="relative z-10 grid h-14 w-14 place-items-center rounded-full border-[5px] border-white bg-[#071A2F] text-[#D5AD57] shadow-[0_0_0_1px_rgba(7,26,47,0.14)] transition duration-300 group-hover:bg-[#8B1E3F] group-hover:text-white">
                                                {method.icon}
                                            </div>

                                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl font-normal text-[#071A2F]/[0.055]">
                                                {method.number}
                                            </span>
                                        </div>

                                        <div className="mt-7">
                                            <span className="text-[9px] font-extrabold uppercase tracking-[0.14em] text-[#A77B2D]">
                                                Stage {method.number}
                                            </span>

                                            <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[25px] font-normal leading-[1.25] text-[#071A2F]">
                                                {method.title}
                                            </h3>

                                            <p className="mt-4 text-[12px] leading-[1.8] text-[#65717D]">
                                                {method.text}
                                            </p>
                                        </div>

                                        <div className="mt-7 flex items-center justify-between border-t border-[#071A2F]/10 pt-5">
                                            <span className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                                {method.outcome}
                                            </span>

                                            <span className="text-lg text-[#A77B2D] transition-transform duration-300 group-hover:translate-x-1">
                                                →
                                            </span>
                                        </div>

                                        <div className="absolute inset-y-0 left-0 w-1 origin-bottom scale-y-0 bg-[#8B1E3F] transition-transform duration-300 group-hover:scale-y-100" />
                                    </article>
                                ))}
                            </div>
                        </div>

                        <div className="mt-12 grid overflow-hidden rounded-[7px_32px_7px_7px] border border-[#071A2F]/10 bg-[#071A2F] text-white lg:grid-cols-[0.72fr_1.28fr]">
                            <div className="relative overflow-hidden px-7 py-9 sm:px-9 lg:px-10 lg:py-11">
                                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full border border-white/10" />

                                <div className="relative">
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                        Connected learning model
                                    </span>

                                    <h3 className="mt-4 max-w-md font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal leading-[1.2]">
                                        Every stage contributes to professional capability.
                                    </h3>
                                </div>
                            </div>

                            <div className="grid border-t border-white/15 sm:grid-cols-3 lg:border-l lg:border-t-0">
                                {[
                                    ["Understand", "Concepts and computing foundations"],
                                    ["Experiment", "Laboratories, projects and research"],
                                    ["Apply", "Industry exposure and career readiness"],
                                ].map(([title, text], index) => (
                                    <div
                                        key={title}
                                        className="border-b border-white/15 p-6 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 lg:p-7"
                                    >
                                        <span className="text-[9px] font-extrabold text-[#D5AD57]">
                                            0{index + 1}
                                        </span>

                                        <strong className="mt-4 block font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal text-white">
                                            {title}
                                        </strong>

                                        <p className="mt-2 text-[11px] leading-6 text-[#AAB6C2]">
                                            {text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* School Outcomes */}
                <section
                    id="school-outcomes"
                    aria-labelledby="school-outcomes-heading"
                    className="relative isolate overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(139,30,63,0.32),transparent_29%)]" />

                        <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[#D5AD57]/[0.07] blur-[110px]" />

                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57]/40 to-transparent" />

                        <svg
                            viewBox="0 0 600 600"
                            fill="none"
                            aria-hidden="true"
                            className="absolute -right-24 top-0 hidden h-[38rem] w-[38rem] text-white/[0.035] lg:block"
                        >
                            <circle cx="300" cy="300" r="220" stroke="currentColor" />
                            <circle cx="300" cy="300" r="155" stroke="currentColor" />
                            <circle cx="300" cy="300" r="85" stroke="currentColor" />
                            <path d="M80 300h440M300 80v440" stroke="currentColor" />
                        </svg>

                        <div className="absolute -bottom-14 right-0 select-none font-sans text-[8rem] font-black leading-none tracking-[-0.08em] text-white/[0.018] sm:text-[13rem] lg:text-[19rem]">
                            IMPACT
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>School outcomes</span>
                                </div>

                                <h2
                                    id="school-outcomes-heading"
                                    className="m-0 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-white"
                                >
                                    Capabilities that create
                                    <em className="font-normal text-[#D5AD57]">
                                        {" "}
                                        meaningful digital impact.
                                    </em>
                                </h2>
                            </div>

                            <p className="m-0 border-l border-white/15 pl-7 text-[15px] leading-[1.85] text-[#B5C0CC] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                Graduates develop the technical depth, professional confidence and
                                innovation mindset required to progress across evolving technology,
                                research and entrepreneurial environments.
                            </p>
                        </header>

                        <div className="mt-16 grid overflow-hidden rounded-[8px_38px_8px_8px] border border-white/15 bg-white/[0.045] backdrop-blur-sm md:grid-cols-2 xl:grid-cols-3">
                            {schoolOutcomes.map((outcome, index) => (
                                <article
                                    key={outcome.number}
                                    className="group relative min-h-[285px] border-b border-white/15 p-7 transition duration-300 hover:bg-white/[0.055] sm:p-8 md:border-r md:even:border-r-0 xl:border-r xl:[&:nth-child(3n)]:border-r-0"
                                >
                                    <div className="flex items-start justify-between gap-5">
                                        <span className="text-[10px] font-extrabold text-[#D5AD57]">
                                            {outcome.number}
                                        </span>

                                        <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl font-normal text-white/[0.035]">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                    </div>

                                    <strong className="mt-8 block font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(32px,3vw,45px)] font-normal leading-none tracking-[-0.035em] text-[#D5AD57]">
                                        {outcome.value}
                                    </strong>

                                    <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[23px] font-normal leading-[1.25] text-white">
                                        {outcome.label}
                                    </h3>

                                    <p className="mt-4 max-w-sm text-[12px] leading-[1.75] text-[#9EABB8]">
                                        {outcome.text}
                                    </p>

                                    <div className="mt-7 h-px w-10 bg-[#8B1E3F] transition-all duration-300 group-hover:w-20 group-hover:bg-[#D5AD57]" />

                                    <div className="absolute inset-y-0 left-0 w-1 origin-bottom scale-y-0 bg-[#8B1E3F] transition-transform duration-300 group-hover:scale-y-100" />
                                </article>
                            ))}
                        </div>

                        <div className="mt-12 grid overflow-hidden rounded-[7px_30px_7px_7px] border border-white/15 bg-[#0C2744] lg:grid-cols-[1fr_auto] lg:items-center">
                            <div className="px-6 py-7 sm:px-9 lg:px-10">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    Future-ready education
                                </span>

                                <h3 className="mt-3 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-[1.3] text-white sm:text-3xl">
                                    Preparing graduates to contribute across technology, research,
                                    enterprise and global digital careers.
                                </h3>
                            </div>

                            <div className="border-t border-white/15 px-6 py-6 sm:px-9 lg:border-l lg:border-t-0">
                                <a
                                    href="#academic-programs"
                                    className="inline-flex min-h-[52px] items-center justify-center gap-6 rounded-full bg-[#D5AD57] px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0C2744]"
                                >
                                    Explore programmes
                                    <span aria-hidden="true">↗</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Faculty & Research Mentors */}
                <Faculty />

                {/* Career Services & Placements */}
                <section
                    id="career-services"
                    aria-labelledby="hospitality-career-heading"
                    className="relative isolate overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_8%,rgba(139,30,63,0.34),transparent_28%)]" />
                        <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[#D5AD57]/[0.07] blur-[110px]" />
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57]/45 to-transparent" />

                        <div className="absolute -bottom-16 right-0 font-sans text-[8rem] font-black leading-none tracking-[-0.08em] text-white/[0.018] sm:text-[13rem] lg:text-[19rem]">
                            CAREER
                        </div>

                        <svg
                            viewBox="0 0 600 600"
                            fill="none"
                            aria-hidden="true"
                            className="absolute -right-24 top-4 hidden h-[38rem] w-[38rem] text-white/[0.035] lg:block"
                        >
                            <circle cx="300" cy="300" r="220" stroke="currentColor" />
                            <circle cx="300" cy="300" r="155" stroke="currentColor" />
                            <path d="M80 300h440M300 80v440" stroke="currentColor" />
                        </svg>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.08fr)_minmax(330px,0.56fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>Career services & placements</span>
                                </div>

                                <h2
                                    id="hospitality-career-heading"
                                    className="m-0 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em]"
                                >
                                    From hospitality education to
                                    <em className="font-normal text-[#D5AD57]">
                                        {" "}
                                        professional excellence.
                                    </em>
                                </h2>
                            </div>

                            <div className="border-l border-white/15 pl-7 max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                <p className="m-0 text-[15px] leading-[1.85] text-[#B5C0CC]">
                                    Career preparation combines industry exposure, professional grooming,
                                    internships and placement support for opportunities across global
                                    hospitality and service sectors.
                                </p>

                                <div className="mt-7 flex flex-wrap gap-3">
                                    <a
                                        href="/placements"
                                        className="inline-flex min-h-[50px] items-center gap-5 rounded-full bg-[#8B1E3F] px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:-translate-y-1 hover:bg-[#D5AD57] hover:text-[#071A2F]"
                                    >
                                        Explore placements <span>↗</span>
                                    </a>

                                    <a
                                        href="/corporate-relations"
                                        className="inline-flex min-h-[50px] items-center gap-5 rounded-full border border-white/20 px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:border-[#D5AD57] hover:text-[#D5AD57]"
                                    >
                                        Industry partnerships <span>→</span>
                                    </a>
                                </div>
                            </div>
                        </header>

                        <div className="mt-16 grid overflow-hidden rounded-[8px_38px_8px_8px] border border-white/15 bg-white/[0.045] sm:grid-cols-2 xl:grid-cols-4">
                            {hospitalityPlacementStats.map((stat) => (
                                <article
                                    key={stat.label}
                                    className="min-h-[165px] border-b border-white/15 p-7 sm:border-r xl:border-b-0 xl:last:border-r-0"
                                >
                                    <strong className="font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,4vw,60px)] font-normal tracking-[-0.05em] text-[#D5AD57]">
                                        {stat.value}
                                        <span className="text-2xl">{stat.suffix}</span>
                                    </strong>

                                    <span className="mt-4 block text-[10px] font-bold uppercase leading-5 tracking-[0.12em] text-white/55">
                                        {stat.label}
                                    </span>
                                </article>
                            ))}
                        </div>

                        <div className="mt-14 grid gap-8 lg:grid-cols-[0.72fr_1.28fr]">
                            <article className="relative overflow-hidden rounded-[7px_34px_7px_7px] bg-[#0C2744] p-7 sm:p-9 lg:p-10">
                                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border border-white/10" />
                                <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full border border-[#D5AD57]/20" />

                                <div className="relative">
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]">
                                        Global hospitality pathways
                                    </span>

                                    <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(30px,3vw,43px)] font-normal leading-[1.18]">
                                        Careers across service, travel and luxury hospitality.
                                    </h3>

                                    <p className="mt-5 text-[13px] leading-[1.8] text-[#AAB6C2]">
                                        Students prepare for opportunities in hotel operations, front
                                        office, culinary production, food and beverage service, tourism,
                                        events, aviation, cruise hospitality and guest experience.
                                    </p>

                                    <div className="mt-8 grid grid-cols-2 border-l border-t border-white/15">
                                        {hospitalityRecruiterTypes.map(([shortName, title]) => (
                                            <div
                                                key={shortName}
                                                className="flex min-h-[88px] items-center gap-3 border-b border-r border-white/15 p-4 transition hover:bg-white/[0.04]"
                                            >
                                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#D5AD57]/35 text-[9px] font-extrabold text-[#D5AD57]">
                                                    {shortName}
                                                </span>

                                                <strong className="text-[11px] leading-5 text-white/80">
                                                    {title}
                                                </strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </article>

                            <div className="rounded-[7px_34px_7px_7px] border border-white/15 bg-white/[0.035] p-7 sm:p-9">
                                <div className="flex items-center justify-between gap-5 border-b border-white/15 pb-5">
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#D5AD57]">
                                        Student career journey
                                    </span>

                                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/35">
                                        Five stages
                                    </span>
                                </div>

                                <div className="relative mt-2">
                                    <div className="absolute bottom-10 left-[19px] top-10 w-px bg-white/15" />

                                    {hospitalityCareerPath.map((step) => (
                                        <article
                                            key={step.number}
                                            className="relative grid grid-cols-[40px_1fr] gap-5 border-b border-white/12 py-6 last:border-b-0"
                                        >
                                            <span className="relative z-10 grid h-10 w-10 place-items-center rounded-full border border-[#D5AD57]/40 bg-[#071A2F] text-[9px] font-extrabold text-[#D5AD57]">
                                                {step.number}
                                            </span>

                                            <div>
                                                <h3 className="m-0 font-['Libre_Caslon_Display',Georgia,serif] text-[22px] font-normal">
                                                    {step.title}
                                                </h3>

                                                <p className="mt-2 text-[12px] leading-[1.7] text-[#9EABB8]">
                                                    {step.text}
                                                </p>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 grid overflow-hidden rounded-[7px_30px_7px_7px] border border-white/15 bg-[#8B1E3F] lg:grid-cols-[1fr_auto] lg:items-center">
                            <div className="px-7 py-7 sm:px-9">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#F1D58E]">
                                    Industry internship programme
                                </span>

                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal sm:text-3xl">
                                    Experience real hospitality environments before graduation.
                                </h3>
                            </div>

                            <div className="border-t border-white/20 px-7 py-6 lg:border-l lg:border-t-0">
                                <a
                                    href="/contact-us"
                                    className="inline-flex min-h-[50px] items-center gap-5 rounded-full bg-white px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8B1E3F] transition hover:-translate-y-1 hover:bg-[#D5AD57] hover:text-[#071A2F]"
                                >
                                    Speak with career services <span>↗</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Admission Process & Eligibility */}
                <section
                    id="admission-process"
                    aria-labelledby="hospitality-admission-heading"
                    className="relative isolate overflow-hidden bg-[#EEE5D3] py-20 text-[#132337] sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(139,30,63,0.1),transparent_28%)]" />
                        <div className="absolute -left-28 bottom-0 h-80 w-80 rounded-full bg-[#D5AD57]/15 blur-[100px]" />
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B1E3F]/30 to-transparent" />

                        <div className="absolute -bottom-14 right-0 font-sans text-[8rem] font-black leading-none tracking-[-0.08em] text-[#071A2F]/[0.025] sm:text-[13rem] lg:text-[19rem]">
                            ADMIT
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.1fr)_minmax(330px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,145px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>Admission process & eligibility</span>
                                </div>

                                <h2
                                    id="hospitality-admission-heading"
                                    className="m-0 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-[#071A2F]"
                                >
                                    Your journey into professional
                                    <em className="font-normal text-[#8B1E3F]">
                                        {" "}
                                        hospitality begins here.
                                    </em>
                                </h2>
                            </div>

                            <p className="m-0 border-l border-[#071A2F]/15 pl-7 text-[15px] leading-[1.85] text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                A transparent admission pathway helps applicants understand each stage,
                                prepare documents and select the programme aligned with their goals.
                            </p>
                        </header>

                        <div className="relative mt-16">
                            <div className="absolute left-[8%] right-[8%] top-[29px] hidden h-px bg-[#BFB3A1] xl:block" />

                            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-6">
                                {hospitalityAdmissionSteps.map((step, index) => (
                                    <article
                                        key={step.number}
                                        className="group relative rounded-[7px_28px_7px_7px] border border-[#071A2F]/10 bg-white/70 p-6 shadow-[0_18px_55px_rgba(7,26,47,0.06)] backdrop-blur-sm transition hover:-translate-y-1 hover:border-[#8B1E3F]/40 hover:bg-white"
                                    >
                                        <div className="relative z-10 grid h-[58px] w-[58px] place-items-center rounded-full border-[5px] border-[#EEE5D3] bg-[#071A2F] text-[10px] font-extrabold text-[#D5AD57] transition group-hover:bg-[#8B1E3F] group-hover:text-white">
                                            {step.number}
                                        </div>

                                        <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-[21px] font-normal leading-[1.25] text-[#071A2F]">
                                            {step.title}
                                        </h3>

                                        <p className="mt-3 text-[11px] leading-[1.7] text-[#65717D]">
                                            {step.text}
                                        </p>

                                        <span className="absolute right-5 top-5 text-[#A77B2D]">
                                            {index < hospitalityAdmissionSteps.length - 1 ? "→" : "✓"}
                                        </span>
                                    </article>
                                ))}
                            </div>
                        </div>

                        <div className="mt-16">
                            <div className="flex flex-col justify-between gap-5 border-b border-[#071A2F]/15 pb-7 sm:flex-row sm:items-end">
                                <div>
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                        Programme eligibility
                                    </span>

                                    <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(30px,3vw,44px)] font-normal text-[#071A2F]">
                                        Choose the academic level that matches your next step.
                                    </h3>
                                </div>

                                <a
                                    href="/admissions"
                                    className="inline-flex w-fit items-center gap-5 border-b border-[#8B1E3F] pb-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8B1E3F]"
                                >
                                    Complete admission guidance <span>↗</span>
                                </a>
                            </div>

                            <div className="mt-8 grid gap-5 md:grid-cols-2">
                                {hospitalityEligibility.map((item, index) => (
                                    <article
                                        key={item.level}
                                        className="group grid overflow-hidden rounded-[7px_30px_7px_7px] border border-[#071A2F]/10 bg-white/70 transition hover:bg-white hover:shadow-[0_25px_70px_rgba(7,26,47,0.09)] sm:grid-cols-[130px_1fr]"
                                    >
                                        <div className="flex flex-col justify-between bg-[#071A2F] p-6 text-white">
                                            <span className="text-[10px] font-extrabold text-[#D5AD57]">
                                                0{index + 1}
                                            </span>

                                            <strong className="mt-10 font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal break-all">
                                                {item.level}
                                            </strong>
                                        </div>

                                        <div className="p-6 sm:p-7">
                                            <span className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                                Programmes
                                            </span>

                                            <h4 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[23px] font-normal leading-[1.25] text-[#071A2F]">
                                                {item.programmes}
                                            </h4>

                                            <div className="mt-5 border-l-2 border-[#D5AD57] pl-4">
                                                <span className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#7A8390]">
                                                    Entry qualification
                                                </span>

                                                <strong className="mt-1 block text-sm text-[#071A2F]">
                                                    {item.qualification}
                                                </strong>
                                            </div>

                                            <p className="mt-5 text-[11px] leading-[1.75] text-[#65717D]">
                                                {item.guidance}
                                            </p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>

                        <div className="mt-10 grid overflow-hidden rounded-[7px_30px_7px_7px] bg-[#8B1E3F] text-white sm:grid-cols-[1fr_auto] sm:items-center">
                            <div className="px-7 py-7 sm:px-9">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#F1D58E]">
                                    Admissions open
                                </span>

                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal sm:text-3xl">
                                    Start your application to the School of Hotel Management.
                                </h3>
                            </div>

                            <div className="border-t border-white/20 px-7 py-6 sm:border-l sm:border-t-0">
                                <a
                                    href="/apply-online"
                                    className="inline-flex min-h-[50px] items-center gap-5 rounded-full bg-white px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8B1E3F] transition hover:bg-[#D5AD57] hover:text-[#071A2F]"
                                >
                                    Apply online <span>↗</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Student Testimonials & Success Stories */}
                <Alumni />

                {/* Frequently Asked Questions (FAQ Accordion) */}
                <section
                    id="hospitality-faq"
                    aria-labelledby="hospitality-faq-heading"
                    className="relative isolate overflow-hidden bg-[#F4E8EC] py-20 text-[#132337] sm:py-24 lg:py-[110px]"
                >
                    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,rgba(139,30,63,0.1),transparent_28%)]" />
                        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#D5AD57]/12 blur-[90px]" />
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B1E3F]/30 to-transparent" />

                        <div className="absolute -bottom-20 right-0 font-sans text-[12rem] font-black leading-none tracking-[-0.08em] text-[#071A2F]/[0.025] sm:text-[18rem] lg:text-[28rem]">
                            FAQ
                        </div>
                    </div>

                    <div className="mx-auto w-[min(1280px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <header className="grid gap-9 lg:grid-cols-[minmax(0,1.1fr)_minmax(330px,0.55fr)] lg:items-end lg:gap-[clamp(55px,9vw,140px)]">
                            <div>
                                <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                    <span className="h-0.5 w-[38px] bg-current" />
                                    <span>Frequently asked questions</span>
                                </div>

                                <h2
                                    id="hospitality-faq-heading"
                                    className="m-0 max-w-[860px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.06] tracking-[-0.04em] text-[#071A2F]"
                                >
                                    Clear answers for your
                                    <em className="font-normal text-[#8B1E3F]">
                                        {" "}
                                        hospitality journey.
                                    </em>
                                </h2>
                            </div>

                            <p className="m-0 border-l border-[#071A2F]/15 pl-7 text-[15px] leading-[1.85] text-[#65717D] max-lg:border-l-0 max-lg:border-t max-lg:pt-7">
                                Explore commonly asked questions about programmes, eligibility,
                                internships, facilities, placements and career opportunities.
                            </p>
                        </header>

                        <div className="mt-16 grid gap-8 lg:grid-cols-[330px_minmax(0,1fr)] lg:items-start">
                            <aside className="relative overflow-hidden rounded-[7px_32px_7px_7px] bg-[#071A2F] p-7 text-white shadow-[0_25px_70px_rgba(7,26,47,0.18)] lg:sticky lg:top-28">
                                <span className="absolute right-4 top-[-30px] font-['Libre_Caslon_Display',Georgia,serif] text-[11rem] leading-none text-white/[0.05]">
                                    ?
                                </span>

                                <div className="relative">
                                    <span className="text-[9px] font-extrabold uppercase tracking-[0.15em] text-[#D5AD57]">
                                        Find your answer
                                    </span>

                                    <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[29px] font-normal leading-[1.25]">
                                        What would you like to know?
                                    </h3>

                                    <label className="mt-7 flex h-12 items-center gap-3 rounded-[5px] border border-white/20 bg-white/[0.06] px-4">
                                        <span className="text-[#D5AD57]">⌕</span>

                                        <span className="sr-only">
                                            Search hotel management frequently asked questions
                                        </span>

                                        <input
                                            type="search"
                                            value={hospitalityFaqSearch}
                                            onChange={(event) => {
                                                setHospitalityFaqSearch(event.target.value);
                                                setOpenHospitalityFaq(0);
                                            }}
                                            placeholder="Search questions..."
                                            className="w-full border-0 bg-transparent text-[11px] text-white outline-none placeholder:text-white/35"
                                        />
                                    </label>

                                    <div className="mt-7 border-t border-white/15">
                                        {[
                                            ["Programmes", "Academic pathways"],
                                            ["Internships", "Industry exposure"],
                                            ["Careers", "Placement support"],
                                            ["Student life", "Facilities and support"],
                                        ].map(([title, description]) => (
                                            <div
                                                key={title}
                                                className="border-b border-white/15 py-4"
                                            >
                                                <strong className="block text-[11px] text-white/85">
                                                    {title}
                                                </strong>

                                                <span className="mt-1 block text-[9px] text-white/35">
                                                    {description}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <a
                                        href="/contact-us"
                                        className="mt-7 flex items-center justify-between border-b border-[#D5AD57] pb-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#D5AD57]"
                                    >
                                        Ask admissions team <span>↗</span>
                                    </a>
                                </div>
                            </aside>

                            <div>
                                <div className="flex items-center justify-between gap-5 border-b border-[#071A2F]/15 px-1 pb-5">
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                        Admission information
                                    </span>

                                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#7A8390]">
                                        {visibleHospitalityFaqs.length} questions
                                    </span>
                                </div>

                                {visibleHospitalityFaqs.length > 0 ? (
                                    visibleHospitalityFaqs.map((item, index) => {
                                        const isOpen = openHospitalityFaq === index;

                                        return (
                                            <article
                                                key={item.question}
                                                className={[
                                                    "border-b border-[#071A2F]/15 transition",
                                                    isOpen ? "bg-white/55" : "bg-transparent",
                                                ].join(" ")}
                                            >
                                                <button
                                                    type="button"
                                                    aria-expanded={isOpen}
                                                    onClick={() =>
                                                        setOpenHospitalityFaq(isOpen ? -1 : index)
                                                    }
                                                    className="grid min-h-[112px] w-full grid-cols-[34px_1fr_44px] items-center gap-4 px-2 py-5 text-left sm:gap-6"
                                                >
                                                    <span className="self-start pt-2 text-[9px] font-extrabold text-[#A77B2D]">
                                                        {String(index + 1).padStart(2, "0")}
                                                    </span>

                                                    <span>
                                                        <small className="mb-2 block text-[8px] font-extrabold uppercase tracking-[0.12em] text-[#8B1E3F]">
                                                            {item.category}
                                                        </small>

                                                        <strong className="font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(18px,2vw,25px)] font-normal leading-[1.35] text-[#071A2F]">
                                                            {item.question}
                                                        </strong>
                                                    </span>

                                                    <span
                                                        className={[
                                                            "relative grid h-11 w-11 place-items-center rounded-full border transition",
                                                            isOpen
                                                                ? "rotate-45 border-[#8B1E3F] bg-[#8B1E3F] text-white"
                                                                : "border-[#071A2F]/20 text-[#071A2F]",
                                                        ].join(" ")}
                                                    >
                                                        +
                                                    </span>
                                                </button>

                                                <div
                                                    className={[
                                                        "grid transition-all duration-300",
                                                        isOpen
                                                            ? "grid-rows-[1fr] opacity-100"
                                                            : "grid-rows-[0fr] opacity-0",
                                                    ].join(" ")}
                                                >
                                                    <div className="overflow-hidden">
                                                        <p className="mb-8 ml-[50px] max-w-[800px] border-l-2 border-[#D5AD57] pl-5 text-[13px] leading-[1.85] text-[#65717D] sm:ml-[66px]">
                                                            {item.answer}
                                                        </p>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="py-16 text-center">
                                        <strong className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">
                                            No matching questions found.
                                        </strong>

                                        <button
                                            type="button"
                                            onClick={() => setHospitalityFaqSearch("")}
                                            className="mx-auto mt-5 block rounded-full border border-[#8B1E3F] px-5 py-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8B1E3F]"
                                        >
                                            Clear search
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Lead Capture & Footer Contact Section */}
                <section
                    id="admission-enquiry"
                    aria-labelledby="hospitality-enquiry-heading"
                    className="relative isolate overflow-hidden bg-[#071A2F] text-white"
                >
                    <div className="absolute inset-0 -z-20">
                        <img
                            src={hospitalityContactBackground}
                            alt=""
                            aria-hidden="true"
                            className="h-full w-full object-cover"
                        />
                    </div>

                    <div className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,rgba(7,26,47,0.98)_0%,rgba(7,26,47,0.94)_48%,rgba(7,26,47,0.72)_100%)]" />

                    <div className="pointer-events-none absolute inset-6 border border-[#D5AD57]/20" />

                    <div className="pointer-events-none absolute -right-16 top-10 font-sans text-[9rem] font-black leading-none tracking-[-0.08em] text-white/[0.025] sm:text-[14rem] lg:text-[21rem]">
                        ENQUIRE
                    </div>

                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-12 py-20 max-sm:w-[calc(100%-28px)] sm:py-24 lg:grid-cols-[0.88fr_1.12fr] lg:items-center lg:gap-[clamp(55px,8vw,115px)] lg:py-[120px]">
                        <div>
                            <div className="mb-5 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#D5AD57]">
                                <span className="h-px w-[38px] bg-current" />
                                <span>Admissions 2026</span>
                            </div>

                            <h2
                                id="hospitality-enquiry-heading"
                                className="m-0 max-w-[720px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(44px,5vw,76px)] font-normal leading-[1.04] tracking-[-0.04em]"
                            >
                                Begin your journey into
                                <em className="block font-normal text-[#D5AD57]">
                                    global hospitality.
                                </em>
                            </h2>

                            <p className="mt-7 max-w-[620px] text-[15px] leading-[1.85] text-white/65">
                                Speak with our admissions team about programmes, eligibility,
                                practical training, internships, fees and career opportunities.
                            </p>

                            <div className="mt-10 border-t border-white/15">
                                {[
                                    ["Call admissions", "+91-9373199999", "tel:+919373199999"],
                                    [
                                        "Email admissions",
                                        "admissions@isbmuniversity.edu.in",
                                        "mailto:admissions@isbmuniversity.edu.in",
                                    ],
                                    ["WhatsApp support", "Start a conversation", "#"],
                                ].map(([label, value, href], index) => (
                                    <a
                                        key={label}
                                        href={href}
                                        className="group grid min-h-[82px] grid-cols-[42px_1fr_auto] items-center gap-4 border-b border-white/15 transition hover:bg-white/[0.04] hover:px-4"
                                    >
                                        <span className="grid h-[42px] w-[42px] place-items-center rounded-full border border-[#D5AD57]/45 text-[10px] font-extrabold text-[#D5AD57]">
                                            0{index + 1}
                                        </span>

                                        <span>
                                            <small className="mb-1 block text-[9px] font-bold uppercase tracking-[0.12em] text-white/40">
                                                {label}
                                            </small>

                                            <strong className="text-sm text-white">{value}</strong>
                                        </span>

                                        <span className="text-[#D5AD57] transition group-hover:translate-x-1">
                                            ↗
                                        </span>
                                    </a>
                                ))}
                            </div>

                            <div className="mt-8 grid gap-5 sm:grid-cols-2">
                                <div className="border-l-2 border-[#D5AD57] pl-4">
                                    <span className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-white/40">
                                        Campus location
                                    </span>

                                    <p className="mt-2 text-[12px] leading-6 text-white/70">
                                        ISBM University, Nawapara Kosmi, Chhura, Gariyaband, Chhattisgarh
                                    </p>
                                </div>

                                <div className="border-l-2 border-[#8B1E3F] pl-4">
                                    <span className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-white/40">
                                        Admission assistance
                                    </span>

                                    <p className="mt-2 text-[12px] leading-6 text-white/70">
                                        Programme guidance, application support and document assistance.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-[8px_40px_8px_8px] border border-white/20 bg-white/[0.94] text-[#132337] shadow-[0_35px_100px_rgba(0,0,0,0.25)] backdrop-blur-xl">
                            <div className="border-b border-[#071A2F]/10 px-6 py-7 sm:px-9">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                    Admission enquiry
                                </span>

                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(28px,3vw,41px)] font-normal leading-[1.2] text-[#071A2F]">
                                    Let our team guide your next step.
                                </h3>
                            </div>

                            <form className="grid gap-5 p-6 sm:grid-cols-2 sm:p-9">
                                {[
                                    ["Full name", "text", "Enter your full name"],
                                    ["Phone number", "tel", "Enter your contact number"],
                                    ["Email address", "email", "Enter your email address"],
                                ].map(([label, type, placeholder]) => (
                                    <label key={label} className="block">
                                        <span className="mb-2 block text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#65717D]">
                                            {label}
                                        </span>

                                        <input
                                            type={type}
                                            placeholder={placeholder}
                                            required
                                            className="h-[52px] w-full rounded-[5px] border border-[#071A2F]/15 bg-white px-4 text-sm text-[#071A2F] outline-none transition placeholder:text-[#071A2F]/30 focus:border-[#8B1E3F] focus:ring-2 focus:ring-[#8B1E3F]/10"
                                        />
                                    </label>
                                ))}

                                <label className="block">
                                    <span className="mb-2 block text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#65717D]">
                                        Preferred programme
                                    </span>

                                    <select
                                        required
                                        defaultValue=""
                                        className="h-[52px] w-full rounded-[5px] border border-[#071A2F]/15 bg-white px-4 text-sm text-[#071A2F] outline-none transition focus:border-[#8B1E3F] focus:ring-2 focus:ring-[#8B1E3F]/10"
                                    >
                                        <option value="" disabled>
                                            Select a programme
                                        </option>
                                        <option>BHMCT</option>
                                        <option>B.Sc. Hotel Management</option>
                                        <option>M.Sc. Hotel Management</option>
                                        <option>Diploma in Hotel Management</option>
                                        <option>Diploma in Culinary Arts</option>
                                        <option>Diploma in Travel & Tourism</option>
                                        <option>Ph.D. Hotel Management</option>
                                    </select>
                                </label>

                                <label className="block sm:col-span-2">
                                    <span className="mb-2 block text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#65717D]">
                                        Your message
                                    </span>

                                    <textarea
                                        rows="4"
                                        placeholder="Tell us how our admissions team can assist you"
                                        className="w-full resize-none rounded-[5px] border border-[#071A2F]/15 bg-white px-4 py-4 text-sm text-[#071A2F] outline-none transition placeholder:text-[#071A2F]/30 focus:border-[#8B1E3F] focus:ring-2 focus:ring-[#8B1E3F]/10"
                                    />
                                </label>

                                <label className="flex items-start gap-3 sm:col-span-2">
                                    <input
                                        type="checkbox"
                                        required
                                        className="mt-1 h-4 w-4 accent-[#8B1E3F]"
                                    />

                                    <span className="text-[10px] leading-5 text-[#65717D]">
                                        I agree to be contacted by the University regarding programmes,
                                        admissions and related information.
                                    </span>
                                </label>

                                <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
                                    <button
                                        type="submit"
                                        className="inline-flex min-h-[54px] items-center justify-center gap-5 rounded-full bg-[#8B1E3F] px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:-translate-y-1 hover:bg-[#071A2F]"
                                    >
                                        Submit enquiry <span>↗</span>
                                    </button>

                                    <a
                                        href="/hotel-management-brochure.pdf"
                                        className="inline-flex min-h-[54px] items-center justify-center gap-5 rounded-full border border-[#071A2F] px-6 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#071A2F] transition hover:-translate-y-1 hover:bg-[#071A2F] hover:text-white"
                                    >
                                        Download brochure <span>↓</span>
                                    </a>
                                </div>
                            </form>

                            <div className="flex flex-col gap-4 border-t border-[#071A2F]/10 bg-[#EEE5D3] px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-9">
                                <span className="text-[10px] font-bold text-[#65717D]">
                                    Quick admission support available
                                </span>

                                <a
                                    href="/apply-online"
                                    className="inline-flex items-center gap-4 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8B1E3F]"
                                >
                                    Apply now <span>↗</span>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-white/15 bg-[#061524]">
                        <div className="mx-auto flex w-[min(1320px,calc(100%-48px))] flex-col gap-5 py-7 text-[10px] text-white/45 max-sm:w-[calc(100%-28px)] sm:flex-row sm:items-center sm:justify-between">
                            <p className="m-0">
                                © {new Date().getFullYear()} ISBM University. All rights reserved.
                            </p>

                            <div className="flex flex-wrap gap-5">
                                <a href="#" className="transition hover:text-[#D5AD57]">
                                    Instagram
                                </a>
                                <a href="#" className="transition hover:text-[#D5AD57]">
                                    Facebook
                                </a>
                                <a href="#" className="transition hover:text-[#D5AD57]">
                                    LinkedIn
                                </a>
                                <a href="#" className="transition hover:text-[#D5AD57]">
                                    YouTube
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

            </main>
            <Footer />
        </>
    );
}

export default HotelManagement;

